import React, {useMemo, useState} from "react";
import {Popover} from "@mui/material";
import DataObjectIcon from "@mui/icons-material/DataObject";
import {useTranslation} from "react-i18next";
import type {Editor} from "@tiptap/core";
import type {PrintoutType} from "crqlar-react-common/dist/models/printout/printout.model";
import VariablePicker from "../../../common/variables/VariablePicker";
import {
    getPrintoutPickerVariables,
    printoutPickerVariablesByReference,
    toPrintoutPickerVariables,
} from "../../variables/printoutPickerVariables";
import {ToolbarIconButton} from "./toolbarControls";

interface Props {
    editor: Editor | null;
    printoutType: PrintoutType | null;
}

const ToolbarVariableMenu = ({editor, printoutType}: Props) => {

    const {t} = useTranslation();
    const [variableMenuAnchorEl, setVariableMenuAnchorEl] = useState<HTMLButtonElement | null>(null);

    const variables = useMemo(
        () => getPrintoutPickerVariables(printoutType),
        [printoutType],
    );
    const variablesByReference = useMemo(
        () => printoutPickerVariablesByReference(variables),
        [variables],
    );
    const variableMenuOpen = Boolean(variableMenuAnchorEl);

    const insertVariable = (reference: string) => {
        const variable = variablesByReference.get(reference);

        setVariableMenuAnchorEl(null);

        if (!editor || !variable) return;

        editor
            .chain()
            .focus()
            .insertPrintoutVariable({
                key: variable.key,
                language: null,
            })
            .run();
    };

    return (
        <>
            <ToolbarIconButton
                active={variableMenuOpen}
                disabled={!editor}
                label={t('Insert variable')}
                onClick={(event) => setVariableMenuAnchorEl(event.currentTarget)}
            >
                <DataObjectIcon fontSize="small"/>
            </ToolbarIconButton>

            <Popover
                open={variableMenuOpen}
                anchorEl={variableMenuAnchorEl}
                onClose={() => setVariableMenuAnchorEl(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                elevation={8}
                slotProps={{paper: {sx: {borderRadius: 2, backgroundColor: 'transparent'}}}}
                sx={(theme) => ({zIndex: theme.zIndex.modal - 1})}
                transitionDuration={0}
            >
                <VariablePicker
                    variables={toPrintoutPickerVariables(variables, t)}
                    onSelect={(picked) => insertVariable(picked.key)}
                />
            </Popover>
        </>
    );
};

export default ToolbarVariableMenu;
